import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import type {ZOrderAction} from '../lib/z-order';

export type {ZOrderAction};

interface ElementContextMenuProps {
  x: number;
  y: number;
  /** Element is already topmost / bottommost among the page's absolute items. */
  atFront: boolean;
  atBack: boolean;
  onAction: (action: ZOrderAction) => void;
  onClose: () => void;
}

function Item({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2 px-3 py-[5px] text-left text-[13px] transition-colors ${
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-default hover:bg-accent'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Right-click layering menu for absolute-positioned elements (Canva-style
 * bring-to-front / send-to-back). Stacking order is driven by the element's
 * `order`; the actions reorder it. Rendered in a portal with a click-catching
 * backdrop and viewport-edge flipping, mirroring TableContextMenu.
 */
export function ElementContextMenu({
  x,
  y,
  atFront,
  atBack,
  onAction,
  onClose,
}: ElementContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [pos, setPos] = useState<{top: number; left: number} | null>(null);
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const {width, height} = el.getBoundingClientRect();
    const GAP = 6;
    let left = x;
    if (left + width + GAP > window.innerWidth) left = x - width;
    left = Math.max(GAP, left);
    let top = y;
    if (top + height + GAP > window.innerHeight) top = y - height;
    top = Math.max(GAP, top);
    // Position is derived from the rendered menu's measured size (flip on
    // viewport overflow), so the setState must happen after layout.

    setPos({top, left});
  }, [x, y]);

  const run = (a: ZOrderAction) => {
    onAction(a);
    onClose();
  };

  return createPortal(
    <>
      <div
        style={{position: 'fixed', inset: 0, zIndex: 9998}}
        onMouseDown={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        style={{
          position: 'fixed',
          top: pos?.top ?? y,
          left: pos?.left ?? x,
          zIndex: 9999,
          visibility: pos ? 'visible' : 'hidden',
        }}
        className="w-48 rounded-lg border border-border bg-background py-1.5 text-sm shadow-xl"
        onContextMenu={(e) => e.preventDefault()}
      >
        <Item onClick={() => run('front')} disabled={atFront}>
          Bring to front
        </Item>
        <Item onClick={() => run('forward')} disabled={atFront}>
          Bring forward
        </Item>
        <Item onClick={() => run('backward')} disabled={atBack}>
          Send backward
        </Item>
        <Item onClick={() => run('back')} disabled={atBack}>
          Send to back
        </Item>
      </div>
    </>,
    document.body,
  );
}
